import { useState } from "preact/hooks";
import { useIsMobile } from "../hooks";

// Lightweight first-run coachmark. Step 1 is a centered welcome modal (branded
// with the app's name + primary color); later steps anchor beside the pill.
// A native, shadow-DOM-friendly stand-in for driver.js — no deps.

const STEPS: Array<{ emoji: string; title: string; body: string }> = [
  {
    emoji: "👋",
    title: "Found a bug or have feedback?",
    body: "Use this widget to tell us — it grabs a screenshot automatically, so issues are quick to fix.",
  },
  {
    emoji: "📷",
    title: "Screenshot, record, or track",
    body: "Tap the camera to capture the page and pin the problem, record a short video, or open chat to see the status of what you've sent.",
  },
];

export function WidgetTour({
  edge,
  topPx,
  appTitle,
  onDone,
}: {
  edge: "left" | "right";
  topPx: number;
  appTitle?: string;
  onDone: () => void;
}) {
  const isMobile = useIsMobile();
  const [step, setStep] = useState(0);
  const isLast = step === STEPS.length - 1;
  const centered = step === 0; // welcome step sits in the middle of the screen
  const s = STEPS[step]!;

  let cls = "mf-tour ";
  let style: Record<string, string> | undefined;
  if (centered) {
    cls += "is-center";
  } else if (isMobile) {
    cls += "is-mobile";
  } else {
    cls += edge === "right" ? "is-right" : "is-left";
    style =
      topPx < 0 ? { top: "50%", transform: "translateY(-50%)" } : { top: `${Math.max(16, topPx)}px` };
    if (edge === "right") style.right = "78px";
    else style.left = "78px";
  }

  return (
    <>
      {centered && <div class="mf-tour-backdrop" onClick={onDone} aria-hidden="true" />}
      <div class={cls} style={style} role="dialog" aria-label="How to send feedback">
        {appTitle ? <div class="mf-tour-brand">{appTitle}</div> : null}
        <div class="mf-tour-emoji" aria-hidden="true">
          {s.emoji}
        </div>
        <div class="mf-tour-title">{s.title}</div>
        <p class="mf-tour-body">{s.body}</p>
        <div class="mf-tour-footer">
          <div class="mf-tour-dots" aria-hidden="true">
            {STEPS.map((_, i) => (
              <span key={i} class={`mf-tour-dot ${i === step ? "is-active" : ""}`} />
            ))}
          </div>
          <div class="mf-tour-actions">
            <button type="button" class="mf-tour-skip" onClick={onDone}>
              Skip
            </button>
            <button
              type="button"
              class="mf-tour-next"
              onClick={() => (isLast ? onDone() : setStep((n) => n + 1))}
            >
              {isLast ? "Got it" : "Next"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
